import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, RefreshCw, ArrowRightCircle, Eye, SquarePen, ShieldCheck, Printer } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { stockTransferService } from '../../services/stockTransferService';
import { StockTransferListDTO, StockTransferStatus } from '../../types';
import './StockTransferPage.css';

const STATUS_LABEL: Partial<Record<StockTransferStatus, string>> = {
  Draft:             'Draft',
  PendingApproval:   'Pending Approval',
  Approved:          'Approved',
  Rejected:          'Rejected',
  TransferredOut:    'Transferred Out',
  PartiallyReceived: 'Partially Received',
  Completed:         'Completed',
  Cancelled:         'Cancelled',
};

const STATUS_CLASS: Partial<Record<StockTransferStatus, string>> = {
  Draft:             'ims-badge st-badge-draft',
  PendingApproval:   'ims-badge st-badge-pending',
  Approved:          'ims-badge st-badge-approved',
  Rejected:          'ims-badge st-badge-rejected',
  TransferredOut:    'ims-badge st-badge-transferredout',
  PartiallyReceived: 'ims-badge st-badge-partiallyreceived',
  Completed:         'ims-badge st-badge-completed',
  Cancelled:         'ims-badge st-badge-cancelled',
};

const StockTransferOutPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();

  const [transfers, setTransfers] = useState<StockTransferListDTO[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(
    (location.state as any)?.message || null
  );

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Show all active transfers (everything except Completed/Cancelled which belong in History)
      const res = await stockTransferService.getAll(1, 200, 'Draft,PendingApproval,Approved,Rejected,TransferredOut,PartiallyReceived');
      setTransfers(res.data?.data?.data ?? []);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColumn[] = [
    { key: 'transferNumber', label: t('stockTransfer.transferNumber'), sortable: true, width: '150px' },
    {
      key: 'transferDate',
      label: t('stockTransfer.transferDate'),
      width: '110px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    { key: 'sourceWarehouseName',      label: t('stockTransfer.source'),      width: '150px' },
    { key: 'destinationWarehouseName', label: t('stockTransfer.destination'),  width: '150px' },
    {
      key: 'status',
      label: t('common.status'),
      width: '150px',
      render: (v) => {
        const s = v as StockTransferStatus;
        return <span className={STATUS_CLASS[s] || 'ims-badge'}>{STATUS_LABEL[s] ?? s}</span>;
      },
    },
    { key: 'itemCount',       label: t('stockTransfer.items'),       width: '70px' },
    { key: 'requestedByName', label: t('stockTransfer.requestedBy'), width: '140px' },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      title: t('common.view'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}`),
      className: 'grid-action-btn-view',
    },
    {
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      title: t('common.edit'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}/edit`),
      className: 'grid-action-btn-edit',
      hidden: (row) => row.status !== 'Draft',
    },
    {
      icon: <ShieldCheck size={16} strokeWidth={1.75} />,
      title: t('stockTransfer.approve'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}`),
      className: 'grid-action-btn-approve',
      hidden: (row) => row.status !== 'PendingApproval',
    },
    {
      icon: <Printer size={16} strokeWidth={1.75} />,
      title: t('stockTransfer.printDispatchNote'),
      onClick: (row) => {
        stockTransferService.openTransferOutPdf(row.id).catch((e: any) => {
          setError(e?.message ?? t('stockTransfer.failedToPrintDispatch'));
        });
      },
      className: 'grid-action-btn-print',
      hidden: (row) => !['TransferredOut', 'PartiallyReceived', 'Completed'].includes(row.status),
    },
  ];

  return (
    <Layout>
      <div className="st-page">
        <div className="ims-list-header">
          <div className="ims-list-header-left">
            <h1 className="ims-list-title">
              <Upload size={22} strokeWidth={1.75} style={{ marginRight: 8 }} />
              {t('stockTransfer.transferOut')}
            </h1>
            <p className="ims-list-subtitle">{t('stockTransfer.transferOutPageDesc')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={loadData} disabled={loading}>
              {loading ? <RefreshCw size={14} strokeWidth={2} /> : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/stock-transfers/create')}>
              <ArrowRightCircle size={16} /> {t('stockTransfer.newTransfer')}
            </button>
          </div>
        </div>

        {successMsg && <div className="ims-alert ims-alert-success">{successMsg}</div>}
        {error      && <div className="ims-alert ims-alert-error">{error}</div>}

        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={transfers}
            loading={loading}
            actions={actions}
            emptyMessage={t('stockTransfer.noTransfers')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default StockTransferOutPage;
